import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { forkJoin, Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  UpperbeltActivity,
  UpperbeltActivityRecord,
  UpperbeltFileCategory,
  UpperbeltUploadKind,
} from "../models";

@Injectable({ providedIn: "root" })
export class UpperbeltFilesService {
  constructor(private readonly http: HttpClient) {}

  getActivities(): Observable<UpperbeltActivity[]> {
    return forkJoin([
      this.http.get<UpperbeltActivityRecord[]>("staticdata/godot.json"),
      this.http.get<UpperbeltActivityRecord[]>("staticdata/unity.json"),
    ]).pipe(
      map(([godotActivities, unityActivities]) =>
        [...godotActivities, ...unityActivities].map((activity) =>
          this.toActivity(activity)
        )
      )
    );
  }

  uploadFile(
    activity: UpperbeltActivity,
    uploadKind: UpperbeltUploadKind,
    fileCategory: UpperbeltFileCategory,
    file: File
  ): Observable<void> {
    const formData = new FormData();
    const renamedFile = this.renameFile(activity.id, uploadKind, file);

    formData.append("file", renamedFile);
    formData.append("activityId", activity.id);
    formData.append("uploadKind", uploadKind);
    formData.append("fileCategory", fileCategory);
    formData.append("type", activity.type);
    formData.append("courseId", activity.courseId);
    formData.append("courseName", activity.courseName);
    formData.append("sequence", activity.sequence.toString());
    formData.append("activitySequence", activity.activitySequence.toString());
    formData.append("activityType", activity.activityType);
    formData.append("name", activity.name);

    return this.http.post<void>("api/upperbelt-files", formData);
  }

  getPdf(
    activity: UpperbeltActivity,
    fileCategory: UpperbeltFileCategory
  ): Observable<Blob> {
    return this.http.get(
      `api/upperbelt-files/${fileCategory}/${activity.id}.pdf`,
      { responseType: "blob" }
    );
  }

  getImage(activity: UpperbeltActivity): Observable<Blob> {
    return this.http.get(`api/upperbelt-files/ninja/${activity.id}/image`, {
      responseType: "blob",
    });
  }

  private toActivity(activity: UpperbeltActivityRecord): UpperbeltActivity {
    return {
      id: activity._id,
      type: activity.type,
      courseId: activity.relationships.data.courseid,
      courseName: activity.relationships.data.coursename,
      sequence: activity.attributes.sequence,
      activitySequence: activity.attributes.activitysequence,
      name: activity.attributes.name,
      activityType: activity.attributes.activitytype,
    };
  }

  private renameFile(
    activityId: string,
    uploadKind: UpperbeltUploadKind,
    file: File
  ): File {
    const extension =
      uploadKind === "pdf" ? "pdf" : this.getImageExtension(file);
    return new File([file], `${activityId}.${extension}`, { type: file.type });
  }

  private getImageExtension(file: File): string {
    const fileExtension = file.name.split(".").pop();
    const mimeExtension = file.type.split("/").pop();
    const extension = fileExtension || mimeExtension || "image";

    return extension.replace(/[^a-z0-9]/gi, "").toLowerCase();
  }
}
